/**
 * El encuadre de la cámara en el celular (KAN-464): el marco del DNI va a lo largo del lado
 * largo del video, y en pantalla se ve solo el marco con un margen chico.
 *
 * Por qué a lo largo: con el celular vertical, un marco apaisado deja la tarjeta en el lado
 * CORTO del sensor (≈1080-1440 px); a lo largo, el doble de píxeles por renglón para el MRZ,
 * el PDF417 y el OCR. Con la pantalla vertical el marco va girado 90° y se avisa que se ponga
 * el celular horizontal; si la página gira con el celular, el video queda apaisado y el marco
 * también, como en la computadora.
 *
 * Con el marco girado, todo lo que mira la tarjeta —la guía en vivo, la banda de lectura, la
 * foto que se entrega— se hace sobre el video «derecho»: el cuadro girado un cuarto de vuelta
 * para que la tarjeta quede apaisada, como si el celular estuviera horizontal. Así la guía y la
 * lectura ven lo mismo que con el celular horizontal, que es como están calibradas.
 *
 * El sentido del giro no se puede saber: con la rotación bloqueada, `screen.orientation` sigue
 * diciendo vertical, y `deviceorientation` en iOS pide un permiso aparte. Se supone el habitual
 * —la tarjeta derecha frente a la persona y el celular con su parte de arriba hacia la izquierda—,
 * y los textos de la guía se dibujan girados en ese sentido. Sostenido al revés, la tarjeta queda
 * cabeza abajo en el video derecho: la guía mide la nitidez en las dos bandas posibles y alterna
 * entre las dos al leer (useGuiaEnVivo), y la lectura de la foto la endereza (prueba las cuatro
 * orientaciones).
 *
 * Funciones puras, salvo `esCelular` (lee el navegador) y `dibujarDerecho` (dibuja en un canvas).
 */
import { marcoDeLaTarjeta } from "./reader/marcoDeLaTarjeta";
import type { Rectangulo } from "./reader/types";

/**
 * Cómo está la tarjeta en el video. 0: apaisada. 90: girada un cuarto de vuelta en el sentido de
 * las agujas del reloj (su parte de arriba, hacia la derecha del video).
 */
export type GiroDelMarco = 0 | 90;

/** Hasta qué lado corto de pantalla (en px CSS) un equipo táctil cuenta como celular. */
export const LADO_CORTO_DE_UN_CELULAR = 600;

/**
 * Si es un celular: táctil (`pointer: coarse`) y con un lado corto de pantalla chico. Una tablet o
 * una computadora con pantalla táctil quedan como en la computadora.
 */
export function esCelular(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  if (!window.matchMedia("(pointer: coarse)").matches) return false;
  const ancho = window.screen?.width || window.innerWidth;
  const alto = window.screen?.height || window.innerHeight;
  return Math.min(ancho, alto) <= LADO_CORTO_DE_UN_CELULAR;
}

/** El marco va girado solo en el celular y con el video vertical: a lo largo de su lado largo. */
export function giroDelMarco(ancho: number, alto: number, celular: boolean): GiroDelMarco {
  return celular && alto > ancho ? 90 : 0;
}

/** Las medidas del video derecho (con la tarjeta apaisada). */
export function medidasDerechas(ancho: number, alto: number, giro: GiroDelMarco): { ancho: number; alto: number } {
  return giro === 90 ? { ancho: alto, alto: ancho } : { ancho, alto };
}

/**
 * Un rectángulo del video derecho, en el video tal como llega. Con el giro de 90°, el video derecho
 * es el video girado un cuarto de vuelta en contra de las agujas: su punto (x, y) es el (ancho − y, x)
 * del video.
 */
export function enElVideo(r: Rectangulo, anchoDelVideo: number, giro: GiroDelMarco): Rectangulo {
  if (giro === 0) return r;
  return { x: anchoDelVideo - (r.y + r.h), y: r.x, w: r.h, h: r.w };
}

/** El marco de la tarjeta en el video tal como llega: girado si el giro lo pide. */
export function marcoEnElVideo(ancho: number, alto: number, giro: GiroDelMarco): Rectangulo {
  const d = medidasDerechas(ancho, alto, giro);
  return enElVideo(marcoDeLaTarjeta(d.ancho, d.alto), ancho, giro);
}

/**
 * El margen de la vista alrededor del marco, por lado y en fracciones del marco: lo justo para
 * ver la mesa alrededor de la tarjeta y embocarla.
 */
export const MARGEN_DE_LA_VISTA = 0.08;

/**
 * La parte del video que se ve en el celular: el marco y un margen chico, sin salirse del video
 * (con un marco del 85 % del video, el margen casi no entra a lo largo, y se recorta).
 */
export function vistaDelMarco(
  marco: Rectangulo,
  ancho: number,
  alto: number,
  margen = MARGEN_DE_LA_VISTA
): Rectangulo {
  const x = Math.max(0, marco.x - marco.w * margen);
  const y = Math.max(0, marco.y - marco.h * margen);
  const x1 = Math.min(ancho, marco.x + marco.w * (1 + margen));
  const y1 = Math.min(alto, marco.y + marco.h * (1 + margen));
  return { x, y, w: x1 - x, h: y1 - y };
}

/** El tamaño más grande con la proporción de la vista que entra en el lugar disponible. */
export function tamanoQueEntra(
  vista: { w: number; h: number },
  disponible: { ancho: number; alto: number }
): { ancho: number; alto: number } {
  if (vista.w <= 0 || vista.h <= 0 || disponible.ancho <= 0 || disponible.alto <= 0) return { ancho: 0, alto: 0 };
  const escala = Math.min(disponible.ancho / vista.w, disponible.alto / vista.h);
  return { ancho: Math.floor(vista.w * escala), alto: Math.floor(vista.h * escala) };
}

/** Un rectángulo del video, en porcentajes de la ventana que se ve (para el CSS). */
export function enPorcentajes(
  r: Rectangulo,
  ventana: Rectangulo
): { left: string; top: string; width: string; height: string } {
  return {
    left: `${((r.x - ventana.x) / ventana.w) * 100}%`,
    top: `${((r.y - ventana.y) / ventana.h) * 100}%`,
    width: `${(r.w / ventana.w) * 100}%`,
    height: `${(r.h / ventana.h) * 100}%`,
  };
}

/**
 * Las esquinas del marco en pantalla. La guía las cuenta en la tarjeta (superior izquierda,
 * superior derecha, inferior derecha, inferior izquierda); con el giro de 90°, la superior
 * izquierda de la tarjeta queda arriba a la derecha del video, y así siguiendo. Devuelve, para
 * cada posición en pantalla en ese mismo orden, qué esquina de la tarjeta cae ahí.
 */
export function esquinaDeLaTarjetaEn(posicion: number, giro: GiroDelMarco): number {
  return giro === 90 ? (posicion + 3) % 4 : posicion;
}

/**
 * Dónde está, en el video derecho (`ancho` × `alto`), la zona `r` de una tarjeta que quedó cabeza
 * abajo: con el celular sostenido al revés.
 */
export function mediaVueltaDe(r: Rectangulo, ancho: number, alto: number): Rectangulo {
  return { x: ancho - (r.x + r.w), y: alto - (r.y + r.h), w: r.w, h: r.h };
}

/**
 * Dibuja un rectángulo del video derecho (`r`) en `destino` (ancho × alto del canvas), girando
 * el video si hace falta. Con el giro de 90° el video se dibuja girado un cuarto de vuelta en
 * contra de las agujas, que es lo que deja la tarjeta apaisada. Con `mediaVuelta`, además se da
 * vuelta: es la zona de una tarjeta cabeza abajo (mediaVueltaDe), que así queda derecha.
 */
export function dibujarDerecho(
  ctx: CanvasRenderingContext2D,
  fuente: CanvasImageSource,
  anchoDelVideo: number,
  r: Rectangulo,
  destino: { ancho: number; alto: number },
  giro: GiroDelMarco,
  mediaVuelta = false
): void {
  const { ancho, alto } = destino;
  if (giro === 0) {
    if (!mediaVuelta) {
      ctx.drawImage(fuente, r.x, r.y, r.w, r.h, 0, 0, ancho, alto);
      return;
    }
    // (x, y) → (ancho − x, alto − y).
    ctx.setTransform(-1, 0, 0, -1, ancho, alto);
    ctx.drawImage(fuente, r.x, r.y, r.w, r.h, 0, 0, ancho, alto);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return;
  }
  const v = enElVideo(r, anchoDelVideo, giro);
  // (x, y) del dibujo sin girar → (y, alto − x): un cuarto de vuelta en contra de las agujas; con
  // media vuelta más, (x, y) → (ancho − y, x).
  if (mediaVuelta) ctx.setTransform(0, 1, -1, 0, ancho, 0);
  else ctx.setTransform(0, -1, 1, 0, 0, alto);
  ctx.drawImage(fuente, v.x, v.y, v.w, v.h, 0, 0, alto, ancho);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
